import * as React from 'react'

import { NOTES_V2_MENTIONS } from '@disco/constants/FeatureTypes'
import type { MentionUser } from '@disco/shared-components/rich-text-editor'
import { loadBusinessUsers } from '@disco/state-management/actions/BusinessUsersActions'
import {
  getBusinessUsers,
  getConstanceSetting,
} from '@disco/state-management/selectors/user'

import { useDispatch, useSelector } from 'hooks/use-redux'

type MentionUsersState = {
  mentionsEnabled: boolean
  users: MentionUser[]
  usersLoading: boolean
}

export const useMentionUsers = (): MentionUsersState => {
  const mentionsEnabled = useSelector(state =>
    Boolean(getConstanceSetting(state, NOTES_V2_MENTIONS)),
  )
  const businessUsers = useSelector(getBusinessUsers)
  const dispatch = useDispatch()
  const loadRequestedRef = React.useRef(false)

  React.useEffect(() => {
    if (
      mentionsEnabled &&
      !businessUsers.loading &&
      !businessUsers.loaded &&
      !loadRequestedRef.current
    ) {
      loadRequestedRef.current = true
      void Promise.resolve(dispatch(loadBusinessUsers())).catch(() => undefined)
    }
  }, [businessUsers.loaded, businessUsers.loading, dispatch, mentionsEnabled])

  const users = React.useMemo<MentionUser[]>(
    () =>
      businessUsers.users
        .filter(({ removed }) => !removed)
        .map(({ user }) => {
          const name = `${user.first_name} ${user.last_name}`.trim()
          return {
            id: user.id,
            label: name || user.email,
            display: name || user.email,
            description: user.email,
          }
        }),
    [businessUsers.users],
  )

  return {
    mentionsEnabled,
    users,
    usersLoading:
      mentionsEnabled && !businessUsers.loaded && !businessUsers.loadError,
  }
}
